import React, { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { toast } from "sonner";
import { Broadcast, Plus, Trash, Stop, Play } from "@phosphor-icons/react";
import {
  fetchLiveStreams,
  createLiveStream,
  startLiveStream,
  stopLiveStream,
  deleteLiveStream,
  fetchAgoraToken,
} from "../../lib/api";

export default function AdminStreams() {
  const [streams, setStreams] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", host_name: "", channel_name: "", scheduled_at: "", thumbnail: "" });

  // publisher state
  const [publishingId, setPublishingId] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | starting | live | error | mock
  const [statusMsg, setStatusMsg] = useState("");
  const clientRef = useRef(null);
  const audioTrackRef = useRef(null);
  const videoTrackRef = useRef(null);
  const previewRef = useRef(null);

  const reload = () => fetchLiveStreams().then(setStreams).catch(() => {});

  useEffect(() => {
    reload();
    return () => {
      stopPublishing(); // cleanup
    };
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Title is required");
    try {
      await createLiveStream(form);
      toast.success("Live stream created");
      setForm({ title: "", description: "", host_name: "", channel_name: "", scheduled_at: "", thumbnail: "" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create stream");
    }
  };

  const onDelete = async (id) => {
    if (publishingId === id) await stopPublishing();
    await deleteLiveStream(id);
    reload();
  };

  const startPublishing = async (stream) => {
    try {
      setPublishingId(stream.id);
      setStatus("starting");
      setStatusMsg("Marking stream as live and requesting token…");
      await startLiveStream(stream.id);
      const t = await fetchAgoraToken(stream.channel_name, 10001, 1);
      if (t.mock) {
        setStatus("mock");
        setStatusMsg("Agora keys are not configured. The stream is marked LIVE for the public site, but no real video feed will be transmitted until AGORA_APP_ID and AGORA_APP_CERTIFICATE are set in backend/.env.");
        reload();
        return;
      }
      const client = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
      client.setClientRole("host");
      clientRef.current = client;
      await client.join(t.app_id, stream.channel_name, t.token, t.uid || null);
      const [mic, cam] = await Promise.all([
        AgoraRTC.createMicrophoneAudioTrack(),
        AgoraRTC.createCameraVideoTrack(),
      ]);
      audioTrackRef.current = mic;
      videoTrackRef.current = cam;
      if (previewRef.current) cam.play(previewRef.current);
      await client.publish([mic, cam]);
      setStatus("live");
      setStatusMsg("Broadcasting…");
      reload();
    } catch (err) {
      setStatus("error");
      setStatusMsg(err?.message || "Failed to start broadcast");
      toast.error(err?.message || "Failed to start broadcast");
    }
  };

  const stopPublishing = async () => {
    try {
      if (videoTrackRef.current) {
        videoTrackRef.current.stop();
        videoTrackRef.current.close();
        videoTrackRef.current = null;
      }
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current.close();
        audioTrackRef.current = null;
      }
      if (clientRef.current) {
        await clientRef.current.leave();
        clientRef.current.removeAllListeners();
        clientRef.current = null;
      }
      if (publishingId) {
        try { await stopLiveStream(publishingId); } catch {}
      }
    } catch {}
    setPublishingId(null);
    setStatus("idle");
    setStatusMsg("");
    reload();
  };

  const activeStream = streams.find((s) => s.id === publishingId);

  return (
    <div data-testid="admin-streams-page" className="space-y-12">
      <div>
        <div className="overline text-[#B79C7B]">Live</div>
        <h1 className="font-serif text-4xl text-ivory mt-2">Broadcast Control</h1>
        <p className="text-[#A1A1AA] mt-3 max-w-2xl">Schedule new live programs, start the camera, and go on air. Viewers on the public site will see the active broadcast on <span className="text-ivory">/live</span>.</p>
      </div>

      {/* Publisher panel */}
      <section className="border border-[#2A2A2D] bg-[#121214]">
        <div className="px-6 py-4 border-b border-[#2A2A2D] flex items-center justify-between">
          <div className="overline text-[#B79C7B] flex items-center gap-2"><Broadcast size={14} weight="duotone" /> Publisher</div>
          {activeStream && <div className="text-sm text-[#A1A1AA]">{activeStream.title} <span className="text-[#71717A]">· {activeStream.channel_name}</span></div>}
        </div>
        <div className="grid lg:grid-cols-2">
          <div className="aspect-video bg-black relative">
            <div ref={previewRef} className="absolute inset-0" />
            {status === "idle" && (
              <div className="absolute inset-0 flex items-center justify-center text-[#71717A] text-sm">Select a stream below and press <span className="mx-1 px-2 py-0.5 bg-[#1A1A1D] text-ivory">Go Live</span></div>
            )}
            {status === "starting" && <div className="absolute inset-0 flex items-center justify-center text-ivory text-sm">{statusMsg}</div>}
            {status === "mock" && (
              <div className="absolute inset-0 flex items-center justify-center text-center px-6">
                <div>
                  <div className="overline text-[#B79C7B] mb-2">Preview Mode</div>
                  <p className="text-sm text-[#F4F0EA] max-w-md leading-relaxed">{statusMsg}</p>
                </div>
              </div>
            )}
            {status === "error" && <div className="absolute inset-0 flex items-center justify-center text-[#FF3B30] text-sm">{statusMsg}</div>}
            {status === "live" && (
              <div className="absolute top-3 left-3 overline text-ivory bg-[#FF3B30] px-2 py-1 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-ivory animate-pulseLive" /> ON AIR
              </div>
            )}
          </div>
          <div className="p-8 flex flex-col justify-center">
            {status === "idle" && <p className="text-sm text-[#A1A1AA]">No active broadcast. Pick a stream from the list below to go live.</p>}
            {(status === "live" || status === "mock" || status === "starting") && activeStream && (
              <div className="space-y-4">
                <div>
                  <div className="overline text-[#71717A]">Currently broadcasting</div>
                  <div className="font-serif text-2xl text-ivory mt-1">{activeStream.title}</div>
                </div>
                <button onClick={stopPublishing} className="inline-flex items-center gap-2 h-11 px-5 bg-[#1A1A1D] border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-ivory transition-colors text-sm tracking-wider" data-testid="admin-stop-stream">
                  <Stop size={16} weight="fill" /> End Broadcast
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Create form */}
      <section className="border border-[#2A2A2D] bg-[#121214] p-8">
        <div className="overline text-[#B79C7B] mb-6">Create new live stream</div>
        <form onSubmit={onCreate} className="grid md:grid-cols-2 gap-4">
          <input className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory" placeholder="Title" data-testid="stream-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory" placeholder="Host name" data-testid="stream-host-input" value={form.host_name} onChange={(e) => setForm({ ...form, host_name: e.target.value })} />
          <input className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory" placeholder="Channel name (optional, auto-generated)" data-testid="stream-channel-input" value={form.channel_name} onChange={(e) => setForm({ ...form, channel_name: e.target.value })} />
          <input className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory" placeholder="Thumbnail URL (optional)" data-testid="stream-thumb-input" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} />
          <textarea className="md:col-span-2 min-h-[80px] bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 py-3 text-ivory" placeholder="Description" data-testid="stream-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button type="submit" className="h-11 bg-[#8A2E2E] hover:bg-[#A33B3B] text-sm tracking-widest flex items-center justify-center gap-2 md:col-span-2" data-testid="create-stream-button">
            <Plus size={16} /> CREATE STREAM
          </button>
        </form>
      </section>

      {/* List */}
      <section>
        <div className="overline text-[#B79C7B] mb-4">All streams</div>
        <div className="border border-[#2A2A2D]">
          <div className="grid grid-cols-[1fr_180px_160px_140px] px-5 py-3 bg-[#121214] border-b border-[#2A2A2D] overline text-[#71717A]">
            <div>Title</div><div>Channel</div><div>Status</div><div className="text-right">Actions</div>
          </div>
          {streams.length === 0 && <div className="p-6 text-[#71717A] text-sm">No streams yet.</div>}
          {streams.map((s) => (
            <div key={s.id} className="grid grid-cols-[1fr_180px_160px_140px] items-center px-5 py-4 border-b border-[#2A2A2D]" data-testid={`stream-row-${s.id}`}>
              <div>
                <div className="text-ivory">{s.title}</div>
                <div className="text-xs text-[#71717A]">{s.host_name}</div>
              </div>
              <div className="text-sm font-mono text-[#A1A1AA]">{s.channel_name}</div>
              <div>
                {s.is_live ? (
                  <span className="overline text-[#FF3B30] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulseLive" />LIVE</span>
                ) : (
                  <span className="overline text-[#71717A]">Off Air</span>
                )}
              </div>
              <div className="flex justify-end gap-2">
                {publishingId === s.id ? (
                  <button onClick={stopPublishing} className="w-9 h-9 flex items-center justify-center border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-ivory" title="Stop" data-testid={`stop-${s.id}`}>
                    <Stop size={14} weight="fill" />
                  </button>
                ) : (
                  <button onClick={() => startPublishing(s)} disabled={!!publishingId} className="w-9 h-9 flex items-center justify-center border border-[#8A2E2E] text-[#8A2E2E] hover:bg-[#8A2E2E] hover:text-ivory disabled:opacity-30 disabled:cursor-not-allowed" title="Go Live" data-testid={`go-live-${s.id}`}>
                    <Play size={14} weight="fill" />
                  </button>
                )}
                <button onClick={() => onDelete(s.id)} className="w-9 h-9 flex items-center justify-center border border-[#2A2A2D] text-[#71717A] hover:text-[#FF3B30] hover:border-[#FF3B30]" title="Delete" data-testid={`delete-stream-${s.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
